import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import WindowedSelect from 'react-windowed-select';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import LeaseApiService from '../../services/apiService';

const REACT_APP_REPORTS = process.env.REACT_APP_REPORTS_URL;
const CHEQUE_PAYMENT_METHODS = ['CHEQUE', 'PDC'];
const EMPTY_DATE_VALUES = ['0', '01-jan-1900'];
const MAX_LENGTHS = { chequeNo: 50, drawnOnBank: 255, refNo: 50, narration: 1000 };

const isBlankValue = (value) => value === null || value === undefined || value === '' || value === '0';

const parseSafeDate = (value) => {
    if (isBlankValue(value)) return null;
    const normalized = String(value).trim();
    if (!normalized || EMPTY_DATE_VALUES.includes(normalized.toLowerCase())) return null;
    const parsed = moment(normalized, ['DD-MMM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
    return parsed.isValid() ? parsed.toDate() : null;
};

const formatDisplayDate = (value) => {
    const parsed = parseSafeDate(value);
    return parsed ? moment(parsed).format('DD-MMM-YYYY') : '-';
};

const isAdminOrFinanceRole = (role) =>
    role === 'FinanceAdmin' || role === 'Finance Admin' || role === ' Finance Admin' || role === 'Finance Admin ';

const validateForm = (form, isChequePayment) => {
    const errors = {};
    const bankAccount = (form.bankAccount || '').trim();
    if (isBlankValue(bankAccount)) {
        errors.bankAccount = 'Select Debit Account.';
    }

    const chequeNo = (form.chequeNo || '').trim();
    const drawnOnBank = (form.drawnOnBank || '').trim();
    const refNo = (form.refNo || '').trim();
    const narration = (form.narration || '').trim();

    if (isChequePayment) {
        if (isBlankValue(chequeNo)) {
            errors.chequeNo = 'Enter Cheque No.';
        }
        if (!form.chequeDate || !moment(form.chequeDate).isAfter(moment('2000-01-01'))) {
            errors.chequeDate = 'Enter Cheque Date.';
        }
        if (isBlankValue(drawnOnBank)) {
            errors.drawnOnBank = 'Enter Drawn On Bank.';
        }
    }

    if (!errors.chequeNo && chequeNo.length > MAX_LENGTHS.chequeNo) {
        errors.chequeNo = `Cheque No must be at most ${MAX_LENGTHS.chequeNo} characters.`;
    }
    if (!errors.drawnOnBank && drawnOnBank.length > MAX_LENGTHS.drawnOnBank) {
        errors.drawnOnBank = `Drawn On Bank must be at most ${MAX_LENGTHS.drawnOnBank} characters.`;
    }
    if (refNo.length > MAX_LENGTHS.refNo) {
        errors.refNo = `Ref No must be at most ${MAX_LENGTHS.refNo} characters.`;
    }
    if (narration.length > MAX_LENGTHS.narration) {
        errors.narration = `Narration must be at most ${MAX_LENGTHS.narration} characters.`;
    }
    return errors;
};

const EditLeaseReceipt = ({ receiptNo, onClose }) => {
    const apiService = useMemo(() => new LeaseApiService(), []);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [header, setHeader] = useState(null);
    const [lines, setLines] = useState([]);
    const [additional, setAdditional] = useState(null);
    const [debitAccounts, setDebitAccounts] = useState([]);
    const [monthClosed, setMonthClosed] = useState(false);
    const [form, setForm] = useState(null);
    const [originalForm, setOriginalForm] = useState(null);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState('');
    const [saving, setSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        loadData();
        //  eslint-disable-next-line react-hooks/exhaustive-deps
    }, [receiptNo]);

    const loadData = async () => {
        setLoading(true);
        setLoadError(null);
        const [receiptResult, dropdownResult] = await Promise.allSettled([
            apiService.getLeaseReceiptByNo(receiptNo),
            apiService.getApiParamater('RECEIPT_DROPDOWN', '?dropdownTypes=DebitAccounts'),
        ]);

        if (dropdownResult.status === 'fulfilled') {
            setDebitAccounts(dropdownResult.value?.data?.dropdowns?.DebitAccounts || []);
        }

        if (receiptResult.status === 'rejected') {
            handleLoadError(receiptResult.reason);
            setLoading(false);
            return;
        }

        const res = receiptResult.value;
        const payload = res?.data?.data;
        if (res?.status === 200 && payload) {
            populateForm(payload);
            checkOperatingMonth(payload?.header?.paymentDate);
        } else {
            setLoadError({ type: 'error', message: res?.data?.message || 'Something went wrong while loading the receipt.' });
        }
        setLoading(false);
    };

    const handleLoadError = (error) => {
        const status = error?.response?.status;
        if (status === 404) {
            setLoadError({ type: 'notfound', message: "Receipt not found or you don't have access to it." });
        } else {
            setLoadError({ type: 'error', message: error?.response?.data?.message || 'Something went wrong while loading the receipt.' });
        }
    };

    const checkOperatingMonth = async (paymentDate) => {
        try {
            const params = new URLSearchParams();
            params.append('Action', 'CheckOperatingMonth');
            params.append('TaxDescription', paymentDate || '');
            const res = await apiService.getApiParamater('LEASE_NO_DATA', `?${params.toString()}`);
            let data = res?.data?.result?.data ?? res?.data?.data;
            if (Array.isArray(data)) data = data[0];
            setMonthClosed(Boolean(data?.isClosed ?? data?.IsClosed));
        } catch (error) {
            console.error('Error checking operating month:', error);
        }
    };

    const populateForm = (payload) => {
        const headerData = payload?.header || {};
        setHeader(headerData);
        setLines(payload?.lines || []);
        setAdditional(payload?.additional || null);
        const initialForm = {
            bankAccount: headerData?.bankAccount || '',
            chequeNo: isBlankValue(headerData?.chequeNo) ? '' : headerData.chequeNo,
            chequeDate: parseSafeDate(headerData?.chequeDate),
            drawnOnBank: headerData?.drawnOnBank || '',
            refNo: headerData?.refNo || '',
            narration: headerData?.narration || '',
        };
        setForm(initialForm);
        setOriginalForm(initialForm);
    };

    const isChequePayment = CHEQUE_PAYMENT_METHODS.includes((header?.paymentMethod || '').trim().toUpperCase());
    const pdcLocked = header?.isChequeCleared === true;

    const blockingReasons = useMemo(() => {
        if (!header) return [];
        const reasons = [];
        if ((header?.chequeCount || 0) > 1) {
            reasons.push('This receipt has multiple cheques and cannot be edited.');
        }
        if (isBlankValue(header?.tenantNo)) {
            reasons.push('No tenant details found for this receipt.');
        }
        if (header?.isInFinancialPeriod === false) {
            reasons.push('Receipt date should be between financial period.');
        }
        if (monthClosed) {
            reasons.push('Month is closed for transactions.');
        }
        return reasons;
    }, [header, monthClosed]);

    const isBlocked = blockingReasons.length > 0;
    const fieldsLocked = isBlocked || saving;
    const chequeFieldsLocked = fieldsLocked || pdcLocked || !isChequePayment;
    const debitAccountLocked = fieldsLocked || pdcLocked;

    const isDirty = useMemo(() => {
        if (!originalForm || !form) return false;
        return Object.keys(originalForm).some((key) => {
            if (key === 'chequeDate') {
                const a = originalForm.chequeDate ? moment(originalForm.chequeDate).format('YYYY-MM-DD') : '';
                const b = form.chequeDate ? moment(form.chequeDate).format('YYYY-MM-DD') : '';
                return a !== b;
            }
            return (originalForm[key] || '') !== (form[key] || '');
        });
    }, [originalForm, form]);

    const handleFieldChange = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setSubmitError('');
    };

    const handleFieldBlur = (name) => {
        if (!form) return;
        const fieldErrors = validateForm(form, isChequePayment);
        setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] }));
    };

    const handleSaveClick = () => {
        const fieldErrors = validateForm(form, isChequePayment);
        setErrors(fieldErrors);
        if (Object.keys(fieldErrors).length > 0) return;
        setShowConfirm(true);
    };

    const handlePrint = () => {
        const token = localStorage.getItem('Token');
        const DB = localStorage.getItem('DataBase');
        if (!token) {
            toast.error('Auth token is missing! Unable to print.');
            return;
        }
        const popupWidth = 1000;
        const popupHeight = 600;
        const popupLeft = (window.screen.width - popupWidth) / 2;
        const popupTop = (window.screen.height - popupHeight) / 2;
        const popup = window.open(
            '',
            'ReportPopup',
            `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},resizable=yes,scrollbars=yes`
        );
        if (!popup) return;
        const form2 = document.createElement('form');
        form2.method = 'POST';
        form2.action = REACT_APP_REPORTS;
        form2.target = 'ReportPopup';
        const inputs = [
            { name: 'authToken', value: token },
            { name: '_db', value: DB },
            { name: 'RecordId', value: '' },
            { name: 'LeaseNo', value: header?.leaseNo },
            { name: 'ReceiptNo', value: receiptNo },
            { name: 'reportName', value: 'LEASEBULKRPT' },
        ];
        inputs.forEach(({ name, value }) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form2.appendChild(input);
        });
        document.body.appendChild(form2);
        form2.submit();
        document.body.removeChild(form2);
    };

    const handleConfirmSave = async () => {
        setSaving(true);
        try {
            const body = {
                bankAccount: (form.bankAccount || '').trim(),
                chequeNo: isChequePayment ? ((form.chequeNo || '').trim() || null) : null,
                chequeDate: isChequePayment && form.chequeDate ? moment(form.chequeDate).format('YYYY-MM-DD') : null,
                drawnOnBank: isChequePayment ? ((form.drawnOnBank || '').trim() || null) : null,
                refNo: (form.refNo || '').trim() || null,
                narration: (form.narration || '').trim() || null,
            };
            const res = await apiService.updateLeaseReceipt(receiptNo, body);
            if (res?.status === 200) {
                setShowConfirm(false);
                toast.success(
                    <div>
                        <div>{`Lease Receipt ${res?.data?.data?.receiptNo || receiptNo} updated successfully`}</div>
                        <button type="button" className="btn btn-link p-0" onClick={handlePrint}>Print</button>
                    </div>
                );
                onClose(true);
                return;
            }
            setSubmitError(res?.data?.message || 'Something went wrong while updating the receipt. Please try again.');
        } catch (error) {
            const status = error?.response?.status;
            const message = error?.response?.data?.message;
            if (status === 400) {
                setSubmitError(message || 'Validation failed.');
            } else if (status === 404) {
                toast.error("Receipt not found or you don't have access to it.");
                onClose(false);
                return;
            } else if (status === 409) {
                toast.error(message || 'Conflict occurred. No changes were saved.');
            } else {
                toast.error('Something went wrong while updating the receipt. Please try again.');
            }
        } finally {
            setSaving(false);
            setShowConfirm(false);
        }
    };

    const handleCancel = () => {
        if (isDirty && !window.confirm('Discard changes?')) return;
        onClose(false);
    };

    const totalAmountReceived = (lines || []).reduce((sum, line) => sum + (Number(line?.amount) || 0), 0);

    return (
        <Modal show size="xl" onHide={handleCancel} backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>Edit Lease Receipt {receiptNo}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="center p-5"><ClipLoader color="#6f42c1" size={35} /></div>
                ) : loadError ? (
                    <div className="text-center p-4">
                        <p>{loadError.message}</p>
                        {loadError.type === 'notfound' ? (
                            <button type="button" className="mainbtnSecondary" onClick={() => onClose(false)}>Close</button>
                        ) : (
                            <button type="button" className="mainbtnGry" onClick={loadData}>Retry</button>
                        )}
                    </div>
                ) : (
                    <>
                        {submitError && (
                            <div className="alert alert-danger">{submitError}</div>
                        )}
                        {isBlocked && (
                            <div className="alert alert-warning">
                                {blockingReasons.map((reason) => <div key={reason}>{reason}</div>)}
                            </div>
                        )}
                        {!isBlocked && pdcLocked && (
                            <div className="alert alert-info">
                                This PDC is already cleared. Cheque details and debit account are locked.
                            </div>
                        )}

                        <div className="form-group grid-contain">
                            <div className="d-flex">
                                <label className="form-label pt-1 start">Receipt No</label>
                                <input type="text" className="p-2 form-control w-75" value={header?.receiptNo || ''} disabled />
                            </div>
                            <div className="d-flex">
                                <label className="form-label pt-1 start">Receipt Type</label>
                                <input type="text" className="p-2 form-control w-75" value={header?.receiptType || ''} disabled />
                            </div>
                            <div className="d-flex">
                                <label className="form-label pt-1 start">Payment Method</label>
                                <input type="text" className="p-2 form-control w-75" value={header?.paymentMethod || ''} disabled />
                            </div>
                            <div className="d-flex">
                                <label className="form-label pt-1 start">Payment Date</label>
                                <input type="text" className="p-2 form-control w-75" value={formatDisplayDate(header?.paymentDate)} disabled />
                            </div>
                            <div className="d-flex">
                                <label className="form-label pt-1 start">Receipt Amount</label>
                                <input type="text" className="p-2 form-control w-75" value={Number(header?.receiptAmount || 0).toFixed(3)} disabled />
                            </div>
                            <div className="d-flex">
                                <label className="form-label pt-1 start">Lease No</label>
                                <input type="text" className="p-2 form-control w-75" value={header?.leaseNo || ''} disabled />
                            </div>
                            <div className="d-flex">
                                <label className="form-label pt-1 start">Lease Status</label>
                                <input type="text" className="p-2 form-control w-75" value={header?.leaseStatus || ''} disabled />
                            </div>
                            <div className="d-flex">
                                <label className="form-label pt-1 start">Tenant No</label>
                                <input type="text" className="p-2 form-control w-75" value={header?.tenantNo || ''} disabled />
                            </div>
                            <div className="d-flex">
                                <label className="form-label pt-1 start">Tenant Name</label>
                                <input type="text" className="p-2 form-control w-75" value={header?.tenantName || ''} disabled />
                            </div>
                            <div className="d-flex">
                                <label className="form-label pt-1 start">Property</label>
                                <input type="text" className="p-2 form-control w-75" value={header?.property_Name || ''} disabled />
                            </div>
                            <div className="d-flex">
                                <label className="form-label pt-1 start">Unit No</label>
                                <input type="text" className="p-2 form-control w-75" value={header?.unit_No || ''} disabled />
                            </div>
                        </div>

                        <div className="mt-3">
                            <div className="mainInvHead pt-1">Allocation</div>
                            <div className="table-container mt-2" style={{ overflowX: 'auto' }}>
                                <table className="table table-bordered">
                                    <thead className="dynoTable">
                                        <tr>
                                            <th>Description</th>
                                            <th>Invoice No</th>
                                            <th>Due Date</th>
                                            <th>Net Amount</th>
                                            <th>Amount Received</th>
                                            <th>Credit Account</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lines?.map((line) => (
                                            <tr key={line?.recordId}>
                                                <td>{line?.description}</td>
                                                <td>{line?.invoiceNo}</td>
                                                <td>{formatDisplayDate(line?.leaseDueDate)}</td>
                                                <td>{Number(line?.netAmount || 0).toFixed(3)}</td>
                                                <td>{Number(line?.amount || 0).toFixed(3)}</td>
                                                <td>{line?.crAcName}</td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td colSpan={4} className="text-end"><strong>Total</strong></td>
                                            <td><strong>{totalAmountReceived.toFixed(3)}</strong></td>
                                            <td></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            {additional && (
                                <div className="mt-1">
                                    Additional Amount: {Number(additional?.additionalAmount || 0).toFixed(3)} - {additional?.additionalAccountName}
                                </div>
                            )}
                        </div>

                        <div className="mt-3">
                            <div className="mainInvHead pt-1">Editable Details</div>
                            <div className="form-group grid-contain mt-2">
                                <div className="d-flex">
                                    <label className="form-label pt-1 start">Debit Account *</label>
                                    <WindowedSelect
                                        className="w-75 p-0"
                                        isDisabled={debitAccountLocked}
                                        menuPortalTarget={document.body}
                                        menuPlacement="auto"
                                        menuShouldScrollIntoView={false}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        value={form?.bankAccount
                                            ? { label: debitAccounts?.find((acc) => acc?.value === form.bankAccount)?.text || header?.bankName, value: form.bankAccount }
                                            : null}
                                        onChange={(selected) => handleFieldChange('bankAccount', selected?.value || '')}
                                        onBlur={() => handleFieldBlur('bankAccount')}
                                        options={debitAccounts?.map((acc) => ({ value: acc?.value, label: acc?.text }))}
                                    />
                                </div>
                                {errors.bankAccount && <div className="text-danger small">{errors.bankAccount}</div>}

                                <div className="d-flex">
                                    <label className="form-label pt-1 start">Cheque No</label>
                                    <input type="text" className="p-2 form-control w-75" maxLength={MAX_LENGTHS.chequeNo}
                                        disabled={chequeFieldsLocked} value={form?.chequeNo || ''}
                                        onChange={(e) => handleFieldChange('chequeNo', e.target.value)}
                                        onBlur={() => handleFieldBlur('chequeNo')} />
                                </div>
                                {errors.chequeNo && <div className="text-danger small">{errors.chequeNo}</div>}

                                <div className="d-flex">
                                    <label className="form-label pt-1 start">Cheque Date</label>
                                    <DatePicker
                                        selected={form?.chequeDate || null}
                                        onChange={(date) => { handleFieldChange('chequeDate', date); handleFieldBlur('chequeDate'); }}
                                        dateFormat="dd-MMM-yyyy"
                                        className="p-2 form-control w-100"
                                        disabled={chequeFieldsLocked}
                                    />
                                </div>
                                {errors.chequeDate && <div className="text-danger small">{errors.chequeDate}</div>}

                                <div className="d-flex">
                                    <label className="form-label pt-1 start">Drawn On Bank</label>
                                    <input type="text" className="p-2 form-control w-75" maxLength={MAX_LENGTHS.drawnOnBank}
                                        disabled={chequeFieldsLocked} value={form?.drawnOnBank || ''}
                                        onChange={(e) => handleFieldChange('drawnOnBank', e.target.value)}
                                        onBlur={() => handleFieldBlur('drawnOnBank')} />
                                </div>
                                {errors.drawnOnBank && <div className="text-danger small">{errors.drawnOnBank}</div>}

                                <div className="d-flex">
                                    <label className="form-label pt-1 start">Ref No</label>
                                    <input type="text" className="p-2 form-control w-75" maxLength={MAX_LENGTHS.refNo}
                                        disabled={fieldsLocked} value={form?.refNo || ''}
                                        onChange={(e) => handleFieldChange('refNo', e.target.value)}
                                        onBlur={() => handleFieldBlur('refNo')} />
                                </div>
                                {errors.refNo && <div className="text-danger small">{errors.refNo}</div>}

                                <div className="d-flex">
                                    <label className="form-label pt-1 start">Narration</label>
                                    <textarea className="p-2 form-control w-100" maxLength={MAX_LENGTHS.narration}
                                        disabled={fieldsLocked} value={form?.narration || ''}
                                        onChange={(e) => handleFieldChange('narration', e.target.value)}
                                        onBlur={() => handleFieldBlur('narration')} />
                                </div>
                                {errors.narration && <div className="text-danger small">{errors.narration}</div>}
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>
            {!loading && !loadError && (
                <Modal.Footer>
                    <button type="button" className="mainbtnSecondary" onClick={handleCancel} disabled={saving}>Cancel</button>
                    {!isBlocked && (
                        <button type="button" className="mainbtn" onClick={handleSaveClick} disabled={saving || !isDirty}>
                            {saving ? <ClipLoader color="#ffffff" size={18} /> : 'Save'}
                        </button>
                    )}
                </Modal.Footer>
            )}

            <Modal
                size="md"
                show={showConfirm}
                onHide={() => !saving && setShowConfirm(false)}
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="center">Confirm Update</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0 text-center">Are you sure you want to update receipt {receiptNo}?</p>
                </Modal.Body>
                <Modal.Footer className="end p-3">
                    <button type="button" className="mainbtnGry" onClick={() => setShowConfirm(false)} disabled={saving}>No</button>
                    <button type="button" className="mainbtnRed" onClick={handleConfirmSave} disabled={saving}>
                        {saving ? <ClipLoader color="#ffffff" size={18} /> : 'Yes'}
                    </button>
                </Modal.Footer>
            </Modal>
        </Modal>
    );
};

export { isAdminOrFinanceRole };
export default EditLeaseReceipt;
